import type { DocNetWidgetId, DocNetDashboardLayout } from 'src/@types/docnet';

export type WidgetSize = 'full' | 'kpi' | 'medium';
export type WidgetCategory = 'fleet' | 'kpi' | 'chart' | 'list';
export type WidgetDataSource =
  | 'dashboard_stats'
  | 'dashboard_trends'
  | 'missions'
  | 'audit'
  | 'issues'
  | 'inbox'
  | 'agents';

export interface WidgetMeta {
  id: DocNetWidgetId;
  titleKey: string;
  icon: string;
  size: WidgetSize;
  category: WidgetCategory;
  dataSource: WidgetDataSource[];
  defaultEnabled: boolean;
  descriptionKey: string;
}

export const WIDGET_CATALOG: WidgetMeta[] = [
  // Fleet
  {
    id: 'agent-fleet-status',
    titleKey: 'widget_agent_fleet',
    icon: 'mdi:robot-outline',
    size: 'full',
    category: 'fleet',
    dataSource: ['agents'],
    defaultEnabled: true,
    descriptionKey: 'widget_agent_fleet_desc',
  },

  // KPIs
  {
    id: 'kpi-issues-open',
    titleKey: 'widget_open_issues',
    icon: 'mdi:alert-circle-outline',
    size: 'kpi',
    category: 'kpi',
    dataSource: ['dashboard_stats'],
    defaultEnabled: true,
    descriptionKey: 'widget_open_issues_desc',
  },
  {
    id: 'kpi-missions-active',
    titleKey: 'widget_active_missions',
    icon: 'mdi:rocket-launch-outline',
    size: 'kpi',
    category: 'kpi',
    dataSource: ['dashboard_stats'],
    defaultEnabled: true,
    descriptionKey: 'widget_active_missions_desc',
  },
  {
    id: 'kpi-agents-enabled',
    titleKey: 'widget_agents_enabled',
    icon: 'mdi:robot-outline',
    size: 'kpi',
    category: 'kpi',
    dataSource: ['dashboard_stats'],
    defaultEnabled: true,
    descriptionKey: 'widget_agents_enabled_desc',
  },
  {
    id: 'kpi-runs-completed',
    titleKey: 'widget_runs_today',
    icon: 'mdi:play-circle-outline',
    size: 'kpi',
    category: 'kpi',
    dataSource: ['dashboard_stats'],
    defaultEnabled: true,
    descriptionKey: 'widget_runs_today_desc',
  },
  {
    id: 'kpi-token-usage',
    titleKey: 'widget_token_usage',
    icon: 'mdi:counter',
    size: 'kpi',
    category: 'kpi',
    dataSource: ['dashboard_stats'],
    defaultEnabled: true,
    descriptionKey: 'widget_token_usage_desc',
  },
  {
    id: 'kpi-pending-approvals',
    titleKey: 'widget_pending_approvals',
    icon: 'mdi:clipboard-check-outline',
    size: 'kpi',
    category: 'kpi',
    dataSource: ['dashboard_stats'],
    defaultEnabled: true,
    descriptionKey: 'widget_pending_approvals_desc',
  },

  // Charts
  {
    id: 'chart-issue-trend',
    titleKey: 'widget_issue_trend',
    icon: 'mdi:chart-line',
    size: 'medium',
    category: 'chart',
    dataSource: ['dashboard_trends'],
    defaultEnabled: true,
    descriptionKey: 'widget_issue_trend_desc',
  },
  {
    id: 'chart-missions-by-status',
    titleKey: 'widget_missions_by_status',
    icon: 'mdi:chart-bar',
    size: 'medium',
    category: 'chart',
    dataSource: ['missions'],
    defaultEnabled: true,
    descriptionKey: 'widget_missions_by_status_desc',
  },
  {
    id: 'chart-email-intake',
    titleKey: 'widget_email_intake',
    icon: 'mdi:chart-line-variant',
    size: 'medium',
    category: 'chart',
    dataSource: ['dashboard_trends'],
    defaultEnabled: false,
    descriptionKey: 'widget_email_intake_desc',
  },
  {
    id: 'chart-issues-by-priority',
    titleKey: 'widget_issues_by_priority',
    icon: 'mdi:chart-bar-stacked',
    size: 'medium',
    category: 'chart',
    dataSource: ['dashboard_trends'],
    defaultEnabled: false,
    descriptionKey: 'widget_issues_by_priority_desc',
  },
  {
    id: 'chart-runs-per-day',
    titleKey: 'widget_agent_runs',
    icon: 'mdi:chart-timeline-variant',
    size: 'medium',
    category: 'chart',
    dataSource: ['dashboard_trends'],
    defaultEnabled: false,
    descriptionKey: 'widget_agent_runs_desc',
  },
  {
    id: 'chart-token-by-agent',
    titleKey: 'widget_token_by_agent',
    icon: 'mdi:chart-donut',
    size: 'medium',
    category: 'chart',
    dataSource: ['dashboard_trends'],
    defaultEnabled: false,
    descriptionKey: 'widget_token_by_agent_desc',
  },

  // Lists
  {
    id: 'list-active-missions',
    titleKey: 'widget_list_active_missions',
    icon: 'mdi:rocket-launch',
    size: 'medium',
    category: 'list',
    dataSource: ['missions'],
    defaultEnabled: true,
    descriptionKey: 'widget_list_active_missions_desc',
  },
  {
    id: 'list-recent-activity',
    titleKey: 'widget_list_recent_activity',
    icon: 'mdi:history',
    size: 'medium',
    category: 'list',
    dataSource: ['audit'],
    defaultEnabled: true,
    descriptionKey: 'widget_list_recent_activity_desc',
  },
  {
    id: 'list-pending-approvals',
    titleKey: 'widget_list_pending_approvals',
    icon: 'mdi:clipboard-check',
    size: 'medium',
    category: 'list',
    dataSource: ['inbox'],
    defaultEnabled: false,
    descriptionKey: 'widget_list_pending_approvals_desc',
  },
  {
    id: 'list-my-open-issues',
    titleKey: 'widget_list_open_issues',
    icon: 'mdi:format-list-checks',
    size: 'medium',
    category: 'list',
    dataSource: ['issues'],
    defaultEnabled: false,
    descriptionKey: 'widget_list_open_issues_desc',
  },
  {
    id: 'list-agents-at-work',
    titleKey: 'widget_list_agents_at_work',
    icon: 'mdi:robot',
    size: 'medium',
    category: 'list',
    dataSource: ['agents'],
    defaultEnabled: false,
    descriptionKey: 'widget_list_agents_at_work_desc',
  },
  {
    id: 'list-blocked-items',
    titleKey: 'widget_list_blocked_items',
    icon: 'mdi:block-helper',
    size: 'medium',
    category: 'list',
    dataSource: ['issues'],
    defaultEnabled: false,
    descriptionKey: 'widget_list_blocked_items_desc',
  },
];

/**
 * Maps frontend widget IDs to backend /dashboard/widgets query keys.
 * Only widgets that need the widgets endpoint are listed here.
 * KPI and chart widgets get data from /stats and /trends instead.
 */
export const WIDGET_BACKEND_KEYS: Partial<Record<DocNetWidgetId, string>> = {
  'agent-fleet-status': 'agent_fleet',
  'list-agents-at-work': 'agents_at_work',
  'list-active-missions': 'active_missions',
  'chart-missions-by-status': 'missions_by_status',
  'list-my-open-issues': 'open_issues',
  'list-blocked-items': 'blocked_items',
  'list-pending-approvals': 'needs_approval',
  'list-recent-activity': 'recent_activity',
};

/**
 * Returns the backend widget keys needed for the currently enabled widgets.
 */
export function getRequiredWidgetKeys(enabledWidgets: DocNetWidgetId[]): string[] {
  const keys: string[] = [];
  for (const id of enabledWidgets) {
    const key = WIDGET_BACKEND_KEYS[id];
    if (key && !keys.includes(key)) {
      keys.push(key);
    }
  }
  return keys;
}

export const DEFAULT_LAYOUT: DocNetDashboardLayout = {
  version: 1,
  enabled_widgets: WIDGET_CATALOG.filter((w) => w.defaultEnabled).map(
    (w) => w.id,
  ),
};

export function getWidgetMeta(id: DocNetWidgetId): WidgetMeta | undefined {
  return WIDGET_CATALOG.find((w) => w.id === id);
}

export function parseLayout(raw: string | null): DocNetDashboardLayout {
  if (!raw) return DEFAULT_LAYOUT;
  try {
    const parsed = JSON.parse(raw) as DocNetDashboardLayout;
    if (!Array.isArray(parsed.enabled_widgets)) return DEFAULT_LAYOUT;
    const knownIds = new Set<string>(WIDGET_CATALOG.map((w) => w.id));
    return {
      version: parsed.version ?? 1,
      enabled_widgets: parsed.enabled_widgets.filter((id) =>
        knownIds.has(id),
      ),
    };
  } catch {
    return DEFAULT_LAYOUT;
  }
}
